import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Paper, Typography, Grid, CircularProgress, Alert, Chip, Button,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  FormControl, InputLabel, Select, MenuItem, Divider, Avatar, List, ListItem, ListItemText
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import { apiService } from '../../utils/api';
import { normalizeImageUrl } from '../../components/Post';
import './Dashboard.css';

const STATUS_LABELS = {
  unhandled: '未対応', in_progress: '対応中', confirmed: '確定', cancelled: 'キャンセル',
};
const statusColor = (s) =>
  s === 'confirmed' ? 'success' : s === 'in_progress' ? 'info' : s === 'cancelled' ? 'error' : 'warning';

const pad2 = (n) => String(n).padStart(2, '0');
const toMonthStr = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
const toDateStr = (y, m, day) => `${y}-${pad2(m + 1)}-${pad2(day)}`;

// "HH:mm" を分に変換
const hhmmToMin = (hhmm) => {
  if (!hhmm) return null;
  const [h, mm] = hhmm.split(':').map(Number);
  return h * 60 + mm;
};

export default function CorporateReservations() {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [posters, setPosters] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null); // "YYYY-MM-DD"

  // 手動予約ダイアログ
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formPoster, setFormPoster] = useState('');
  const [formStart, setFormStart] = useState('');
  const [formCourse, setFormCourse] = useState(60);
  const [formName, setFormName] = useState('');
  const [formPhone, setFormPhone] = useState('');
  const [formHotel, setFormHotel] = useState('');
  const [formMemo, setFormMemo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [shiftPosters, setShiftPosters] = useState([]); // 選択日にシフトがあるユーザー

  const month = toMonthStr(current);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.get('/api/corporate/reservation-management', {
        params: { month },
      });
      const data = response?.data || response || {};
      setPosters(data.posters || []);
      setShifts(data.shifts || []);
      setReservations(data.reservations || []);
    } catch (err) {
      setError(err?.response?.data?.error || '予約管理データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const y = current.getFullYear();
  const m = current.getMonth();
  const firstDay = new Date(y, m, 1).getDay();
  const daysInMonth = new Date(y, m + 1, 0).getDate();

  // 日付ごとの集計
  const shiftsByDate = {};
  shifts.forEach((s) => {
    (shiftsByDate[s.date] = shiftsByDate[s.date] || []).push(s);
  });
  const resByDate = {};
  reservations.forEach((r) => {
    (resByDate[r.reservation_date] = resByDate[r.reservation_date] || []).push(r);
  });

  // 前月より前には戻れない（データ保持が前月まで）
  const today = new Date();
  const minMonthValue = today.getFullYear() * 12 + (today.getMonth() - 1); // 前月
  const canGoPrev = (y * 12 + m) > minMonthValue;
  const prevMonth = () => { if (canGoPrev) setCurrent(new Date(y, m - 1, 1)); };
  const nextMonth = () => setCurrent(new Date(y, m + 1, 1));

  const openManualDialog = (dateStr) => {
    setSelectedDate(dateStr);
    setFormPoster('');
    setFormStart('');
    setFormCourse(60);
    setFormName('');
    setFormPhone('');
    setFormHotel('');
    setFormMemo('');
    setShiftPosters([]);
    setDialogOpen(true);
    // その日にシフトがあるユーザーのみ選択可能にする
    apiService.get('/api/corporate/shift-posters', { params: { date: dateStr } })
      .then((res) => {
        const data = res?.data || res || {};
        setShiftPosters(data.posters || []);
      })
      .catch(() => setShiftPosters([]));
  };

  const handleCreateManual = async () => {
    if (!formPoster || !selectedDate || !formStart) {
      alert('対象ユーザー・日付・開始時間は必須です');
      return;
    }
    try {
      setSubmitting(true);
      const response = await apiService.post('/api/corporate/reservations', {
        poster_id: formPoster,
        reservation_date: selectedDate,
        start_time: formStart,
        course_minutes: formCourse,
        reserver_name: formName,
        reserver_phone: formPhone,
        hotel: formHotel,
        memo: formMemo,
      });
      const data = response?.data || response || {};
      if (data.reservation) {
        setReservations((prev) => [...prev, data.reservation]);
        setDialogOpen(false);
      } else {
        alert(data.message || '予約の登録に失敗しました');
      }
    } catch (err) {
      alert(err?.response?.data?.message || '予約の登録に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const selectedShifts = selectedDate ? (shiftsByDate[selectedDate] || []) : [];
  const selectedRes = selectedDate ? (resByDate[selectedDate] || []) : [];
  const posterName = (pid) => (posters.find((p) => p.id === pid) || {}).display_name || '不明';
  const posterAvatar = (pid) => (posters.find((p) => p.id === pid) || {}).avatar || '';

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Typography variant="h4" gutterBottom>予約管理</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        タグ付けしている投稿ユーザーのシフトと予約状況を確認できます。日付をクリックすると詳細と手動予約入力ができます。
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* 月ナビゲーション */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
          <IconButton onClick={prevMonth} disabled={!canGoPrev}><ChevronLeftIcon /></IconButton>
          <Typography variant="h6">{y}年 {m + 1}月</Typography>
          <IconButton onClick={nextMonth}><ChevronRightIcon /></IconButton>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : (
          <Grid container columns={7} spacing={0.5}>
            {['日', '月', '火', '水', '木', '金', '土'].map((w) => (
              <Grid item xs={1} key={w} sx={{ textAlign: 'center', fontWeight: 700, py: 0.5 }}>
                {w}
              </Grid>
            ))}
            {cells.map((d, idx) => {
              if (d === null) return <Grid item xs={1} key={`e${idx}`} />;
              const dateStr = toDateStr(y, m, d);
              const sCount = (shiftsByDate[dateStr] || []).length;
              const rCount = (resByDate[dateStr] || []).length;
              const isSelected = selectedDate === dateStr;
              return (
                <Grid item xs={1} key={dateStr}>
                  <Box
                    onClick={() => setSelectedDate(dateStr)}
                    sx={{
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      minHeight: 64,
                      p: 0.5,
                      cursor: 'pointer',
                      backgroundColor: isSelected ? 'primary.light' : (sCount > 0 ? '#f1f8e9' : 'transparent'),
                      '&:hover': { backgroundColor: 'action.hover' },
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>{d}</Typography>
                    {sCount > 0 && (
                      <Typography variant="caption" sx={{ display: 'block', color: 'success.main' }}>
                        出勤{sCount}
                      </Typography>
                    )}
                    {rCount > 0 && (
                      <Typography variant="caption" sx={{ display: 'block', color: 'primary.main' }}>
                        予約{rCount}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Paper>

      {/* 日別詳細 */}
      {selectedDate && (
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6">{selectedDate} の詳細</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => openManualDialog(selectedDate)}>
              予約を手動入力
            </Button>
          </Box>

          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>出勤タイムライン（予約が入っている時間は塗りつぶし）</Typography>
          {selectedShifts.length === 0 ? (
            <Typography variant="body2" color="text.secondary">この日のシフトはありません</Typography>
          ) : (
            <Box>
              {/* シフトを持つユーザーごとにタイムライン行を表示 */}
              {Array.from(new Set(selectedShifts.map((s) => s.owner_id))).map((ownerId) => {
                const ownerShifts = selectedShifts.filter((s) => s.owner_id === ownerId);
                // このユーザーのシフト全体の範囲を算出（軸の最小・最大）
                let axisStart = 24 * 60, axisEnd = 0;
                ownerShifts.forEach((s) => {
                  const st = hhmmToMin(s.start_time);
                  let en = hhmmToMin(s.end_time);
                  if (s.is_next_day || (en !== null && st !== null && en < st)) en += 24 * 60;
                  if (st !== null) axisStart = Math.min(axisStart, st);
                  if (en !== null) axisEnd = Math.max(axisEnd, en);
                });
                if (axisEnd <= axisStart) { axisStart = 0; axisEnd = 24 * 60; }
                const axisRange = axisEnd - axisStart;

                // このユーザーの予約
                const ownerRes = selectedRes.filter((r) => r.poster_id === ownerId);

                const pct = (min) => `${((min - axisStart) / axisRange) * 100}%`;

                return (
                  <Box key={ownerId} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Avatar src={normalizeImageUrl(posterAvatar(ownerId))} sx={{ width: 24, height: 24 }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{posterName(ownerId)}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {ownerShifts.map((s) => `${s.start_time}〜${s.end_time}`).join(', ')}
                      </Typography>
                    </Box>
                    {/* タイムラインバー */}
                    <Box sx={{ position: 'relative', height: 28, borderRadius: 1, backgroundColor: '#e8f5e9', border: '1px solid #c8e6c9', overflow: 'hidden' }}>
                      {/* 予約時間帯の塗りつぶし */}
                      {ownerRes.map((r) => {
                        const rs = hhmmToMin(r.start_time);
                        let re = hhmmToMin(r.end_time);
                        if (re !== null && rs !== null && re < rs) re += 24 * 60;
                        if (rs === null || re === null) return null;
                        const left = Math.max(axisStart, rs);
                        const right = Math.min(axisEnd, re);
                        if (right <= left) return null;
                        const bg = r.status === 'cancelled' ? 'rgba(158,158,158,0.6)' : 'rgba(123,31,162,0.65)';
                        return (
                          <Box
                            key={r.id}
                            title={`${r.start_time}〜${r.end_time} ${r.reserver_name || ''}（クリックで詳細）`}
                            onClick={() => navigate(`/reservations/${r.id}`)}
                            sx={{
                              position: 'absolute',
                              top: 0, bottom: 0,
                              left: pct(left),
                              width: `${((right - left) / axisRange) * 100}%`,
                              backgroundColor: bg,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              cursor: 'pointer',
                            }}
                          >
                            <Typography variant="caption" sx={{ color: '#fff', fontSize: 10, whiteSpace: 'nowrap', px: 0.5 }}>
                              {r.start_time}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                    {/* 時間目盛（開始・終了） */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.25 }}>
                      <Typography variant="caption" color="text.secondary">
                        {pad2(Math.floor((axisStart % (24 * 60)) / 60))}:{pad2(axisStart % 60)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {pad2(Math.floor((axisEnd % (24 * 60)) / 60))}:{pad2(axisEnd % 60)}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}

          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>予約</Typography>
          {selectedRes.length === 0 ? (
            <Typography variant="body2" color="text.secondary">この日の予約はありません</Typography>
          ) : (
            <List dense>
              {selectedRes.map((r) => (
                <ListItem key={r.id} alignItems="flex-start" sx={{ flexDirection: 'column', alignItems: 'stretch', border: '1px solid #eee', borderRadius: 1, mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Chip label={r.status_label || '未対応'} size="small" color={statusColor(r.status)} />
                    {r.manual && <Chip label="手動" size="small" variant="outlined" />}
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {r.start_time} 〜 {r.end_time}（{r.course_minutes}分）
                    </Typography>
                  </Box>
                  <Typography variant="body2">対象: {r.poster_display_name}</Typography>
                  <Typography variant="body2">予約者: {r.reserver_name || '—'}</Typography>
                  {r.reserver_phone !== undefined && (
                    <Typography variant="body2" color="text.secondary">
                      TEL: {r.reserver_phone || '（未登録）'}
                    </Typography>
                  )}
                  {r.hotel && <Typography variant="body2" color="text.secondary">ホテル（場所）: {r.hotel}</Typography>}
                  {r.memo && <Typography variant="body2" color="text.secondary">その他要望: {r.memo}</Typography>}
                  {r.cancel_requested && <Chip label="キャンセル依頼あり" size="small" color="warning" sx={{ mt: 0.5, alignSelf: 'flex-start' }} />}
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{ mt: 1, alignSelf: 'flex-start' }}
                    onClick={() => navigate(`/reservations/${r.id}`)}
                  >
                    予約詳細・編集
                  </Button>
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
      )}

      {/* 手動予約入力ダイアログ */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>予約を手動入力（{selectedDate}）</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="poster-label">対象ユーザー（この日に出勤予定のみ）</InputLabel>
                <Select
                  labelId="poster-label"
                  label="対象ユーザー（この日に出勤予定のみ）"
                  value={formPoster}
                  onChange={(e) => setFormPoster(e.target.value)}
                >
                  {shiftPosters.map((p) => (
                    <MenuItem key={p.id} value={p.id}>{p.display_name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {shiftPosters.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  この日に出勤予定（シフト登録）のあるタグ付けユーザーがいません。
                </Typography>
              )}
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth label="開始時間" type="time"
                value={formStart} onChange={(e) => setFormStart(e.target.value)}
                InputLabelProps={{ shrink: true }} inputProps={{ step: 600 }}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel id="course-label">コース</InputLabel>
                <Select
                  labelId="course-label"
                  label="コース"
                  value={formCourse}
                  onChange={(e) => setFormCourse(Number(e.target.value))}
                >
                  {Array.from({ length: 34 }, (_, i) => 30 + i * 10).map((min) => (
                    <MenuItem key={min} value={min}>{min}分</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="予約者名" value={formName} onChange={(e) => setFormName(e.target.value)} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="電話番号" value={formPhone} onChange={(e) => setFormPhone(e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="ホテル（場所）" value={formHotel} onChange={(e) => setFormHotel(e.target.value)} placeholder="例: 〇〇ホテル 101号室 など" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="その他要望" multiline rows={2} value={formMemo} onChange={(e) => setFormMemo(e.target.value)} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>キャンセル</Button>
          <Button variant="contained" onClick={handleCreateManual} disabled={submitting || !formPoster || !formStart}>
            {submitting ? '登録中...' : '予約を登録'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
